import type {
  ApprovingInvoiceReportFormInputResponse,
  InvoiceReportReceiptType,
} from 'ap-openapi';
import { useCallback } from 'react';
import {
  ActiveFileType,
  TAB_KEYS,
  usePaymentDetail,
  type TabKey,
} from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import type { Props as HeaderProps } from '@/components/InvoiceReportDetailDrawer/RightContent/Preview/Header';
import {
  getString,
  isRecord,
  isReportInvoiceFile,
  isValidScanDecision,
  normalizeBusinessDocument,
  normalizeInvoiceFile,
  splitFileNameExtension,
  type UnifiedDocument,
} from '@/components/InvoiceReportDetailDrawer/helpers';

interface UsePdfPreviewOptions {
  baseURL?: string;
}

export const usePdfPreview = (options: UsePdfPreviewOptions = {}) => {
  const baseURL = options.baseURL || location.origin;
  const {
    setPdfViewerProps,
    setActiveTab,
    setIsRightPaneCollapsed,
    activeFile,
    setActiveFile,
  } = usePaymentDetail();

  // Unified function to process any document (invoice or business document)
  const processUnifiedDocument = useCallback(
    (
      unifiedDoc: UnifiedDocument,
      receiptType?: InvoiceReportReceiptType,
      eDocMeta?: unknown,
      scanDecision?: 'warning' | 'error' | 'ok',
      showTimestamp?: boolean
    ): HeaderProps | undefined => {
      const { file, title } = unifiedDoc;

      // Use the provided eDocMeta or fall back to the file's own e_doc_meta_datum
      const metaToUse = eDocMeta || file.e_doc_meta_datum;

      if (!metaToUse || typeof metaToUse !== 'object') {
        return {
          title: title || file.name,
          timestamp: showTimestamp || false,
          pdfUrl: file.cache_content_url,
          receiptType: receiptType || file.receipt_type,
        };
      }

      const eDocMetaObj = isRecord(metaToUse) ? metaToUse : {};

      const eDocMetaProps = {
        dpi: getString(eDocMetaObj.dpi_text),
        pixelNumber:
          getString(eDocMetaObj.pixel_number_text) ||
          getString(eDocMetaObj.width_and_height_pixel_text),
        imageDepth: getString(eDocMetaObj.rgb_depth_text),
        isScan: Boolean(eDocMetaObj.is_scan),
        isSatisfyResolutionRequirement: Boolean(
          eDocMetaObj.is_satisfy_resolution_requirement
        ),
        isSatisfyDepthRequirements: Boolean(
          eDocMetaObj.is_satisfy_depth_requirements
        ),
      };

      return {
        title: title || file.name,
        timestamp: showTimestamp || false,
        scanDecision: isValidScanDecision(scanDecision)
          ? scanDecision
          : undefined,
        pdfUrl: file.cache_content_url,
        eDocMeta: eDocMetaProps,
        receiptType: receiptType || file.receipt_type,
      };
    },
    []
  );

  // New unified function to create preview props for any document type
  const createUnifiedPreviewProps = useCallback(
    (
      unifiedDoc: UnifiedDocument,
      receiptType?: InvoiceReportReceiptType,
      eDocMeta?: unknown,
      scanDecision?: 'warning' | 'error' | 'ok',
      showTimestamp?: boolean
    ): HeaderProps | undefined => {
      return processUnifiedDocument(
        unifiedDoc,
        receiptType,
        eDocMeta,
        scanDecision,
        showTimestamp
      );
    },
    [processUnifiedDocument]
  );

  // New unified function to show preview for any document type
  const showUnifiedPreview = useCallback(
    (
      pdfViewerProps: HeaderProps,
      tabKey: TabKey = TAB_KEYS.ATTACHED_INVOICE
    ) => {
      setPdfViewerProps(pdfViewerProps);
      setActiveTab(tabKey);
      setIsRightPaneCollapsed(false);
    },
    [setPdfViewerProps, setActiveTab, setIsRightPaneCollapsed]
  );

  // New function to select and show preview for a specific file
  const selectAndShowFile = useCallback(
    (
      fileId: string,
      fileType: ActiveFileType,
      pdfViewerProps: HeaderProps,
      tabKey: TabKey,
      mfFileId?: string
    ) => {
      // For business documents, fileId is the business document ID and mfFileId is the file ID
      // For invoices, fileId is the file ID (mfFileId is not provided)
      const activeFileData =
        fileType === ActiveFileType.BUSINESS_DOCUMENT && mfFileId
          ? { type: fileType, id: fileId, fileId: mfFileId }
          : { type: fileType, id: fileId };

      // Set this file as active
      setActiveFile(activeFileData);

      // Show the preview
      showUnifiedPreview(pdfViewerProps, tabKey);
    },
    [setActiveFile, showUnifiedPreview]
  );

  const getInvoiceFileForDisplay = useCallback(
    (item: ApprovingInvoiceReportFormInputResponse) => {
      const mfFile = item?.input_additional_values?.[0];

      if (!mfFile || !isReportInvoiceFile(mfFile)) return null;

      const fileNameParts = splitFileNameExtension(mfFile?.name ?? '');
      const href = mfFile?.invoice_number
        ? `${baseURL}/received_invoices?id=${mfFile?.invoice_number}`
        : '';

      return {
        ...mfFile,
        href,
        fileNameParts,
      };
    },
    [baseURL]
  );

  return {
    processUnifiedDocument,
    createUnifiedPreviewProps,
    showUnifiedPreview,
    selectAndShowFile,
    normalizeInvoiceFile,
    normalizeBusinessDocument,
    getInvoiceFileForDisplay,
    activeFile,
  };
};
